import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import { FC, useContext, useState } from 'react';
import { CoupomAPI } from '../../api';
import {
  CoupomContextValue,
  CoupomFormContext,
  CoupomFormContextDefaultValue,
} from '../../context/CreateCoupomFormContext';
import { CreateListContext } from '../../context/ListContext';
import CoupomForm from '../CoupomForm';
import CoupomVtexList from './VtexList';

type CreateCoupomModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const CreateCoupomModal: FC<CreateCoupomModalProps> = ({ isOpen, onClose }) => {
  const toast = useToast();
  const [formContext, setFormContext] = useState<CoupomContextValue>(
    CoupomFormContextDefaultValue,
  );
  const { updateListFn, paginationInfo } = useContext(CreateListContext)[0];
  const [isLoading, setIsLoading] = useState(false);
  const resetContext = () => {
    setFormContext(CoupomFormContextDefaultValue);
  };

  const handleCloseModal = () => {
    onClose();
    const timeout = setTimeout(() => {
      clearTimeout(timeout);
      resetContext();
      updateListFn(paginationInfo.page, paginationInfo.limit);
    }, 500);
  };

  const handleCreate = async () => {
    setIsLoading(true);
    const coupomAPI = new CoupomAPI();
    await coupomAPI
      .create({
        coupom_source_id: formContext.vtexCoupom.idCalculatorConfiguration,
        source: 'vtex',
        customization: {
          active_coupon: formContext.activeCoupom,
          active_countdown: formContext.countdown,
          rules: formContext.rules
            .filter(rule => rule !== '')
            .map(rule => rule.trim()),
          description: formContext.description?.trim(),
          vtex_query: formContext.queryVtex?.trim(),
        },
      })
      .then(() => {
        toast({
          title: 'Cupon criado',
          description: `O cupom ${formContext.vtexCoupom.name} foi criado com sucesso!`,
          status: 'success',
          position: 'top-right',
        });
        resetContext();
      })
      .catch(err =>
        toast({
          title: 'Erro ao criar cupom',
          description: `Ocorreu um erro ao criar o cupom: ${err.response.data.message}`,
          status: 'error',
          position: 'top-right',
        }),
      )
      .finally(() => setIsLoading(false));
  };

  return (
    <>
      <CoupomFormContext.Provider value={[formContext, setFormContext]}>
        <Modal
          isOpen={isOpen}
          onClose={handleCloseModal}
          closeOnOverlayClick={false}
          closeOnEsc={false}
          size="xl"
        >
          <ModalOverlay />
          <ModalContent pb="6" maxH="90svh" my="auto">
            <ModalHeader>Novo Cupom</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {formContext.hasVtexCoupom ? (
                <CoupomForm />
              ) : (
                <Text size="sm">
                  Nenhum cupom selecionado. Por favor, selecione um cupom abaixo
                </Text>
              )}
            </ModalBody>
            {formContext.hasVtexCoupom && (
              <ModalFooter w="full">
                <Button
                  colorScheme="blue"
                  variant="outline"
                  justifySelf="start"
                  mr={3}
                  onClick={() => resetContext()}
                >
                  Escolher outro cupom
                </Button>
                <Button
                  colorScheme="blue"
                  variant="outline"
                  mr={3}
                  onClick={onClose}
                >
                  Cancelar
                </Button>
                <Button
                  rightIcon={
                    isLoading ? (
                      <Spinner
                        thickness="2px"
                        speed="0.65s"
                        emptyColor="gray.200"
                        color="blue.500"
                        size="sm"
                      />
                    ) : undefined
                  }
                  onClick={handleCreate}
                  variant="solid"
                  colorScheme="blue"
                >
                  Criar
                </Button>
              </ModalFooter>
            )}
            {!formContext.hasVtexCoupom && (
              <ModalBody mt="5">
                <CoupomVtexList />
              </ModalBody>
            )}
          </ModalContent>
        </Modal>
      </CoupomFormContext.Provider>
    </>
  );
};

export default CreateCoupomModal;
